// Comparison content — one entry per competitor. Feeds /vs/:slug.
// Kept factually neutral / defensible. No claims that can't be verified.
export const COMPARISONS = [
  {
    slug: 'ilovepdf',
    competitor: 'iLovePDF',
    title: 'Go Convert Documents vs iLovePDF',
    description:
      'iLovePDF vs Go Convert Documents — comparison of pricing, daily limits, watermarks, privacy, and available tools in 2026.',
    keywords: 'ilovepdf alternative, ilovepdf vs, ilovepdf free comparison, best pdf converter',
    intro:
      'iLovePDF is one of the most polished PDF platforms on the web. Its free tier hits a small handful of tasks per hour and asks you to upgrade for large PDFs. Go Convert Documents takes the opposite approach — every tool is unlimited and free, in exchange for a slightly simpler feature set.',
    rows: [
      ['Price for full toolkit', '$4–$9 / month', '$0 — always free'],
      ['Daily conversion limit', 'Limited (free tier)', 'None'],
      ['Signup required', 'Only for paid features', 'Never'],
      ['Watermark on output', 'On some free tools', 'Never'],
      ['File retention', 'Varies', 'Auto-deleted after 1 hour'],
      ['Max file size (free)', '~25 MB', '100 MB'],
      ['PDF ↔ Office', 'Yes', 'Yes'],
      ['OCR for scanned PDFs', 'Paid tier', 'Free'],
      ['Password-protect PDF', 'Yes', 'Yes'],
      ['Watermark PDF', 'Yes', 'Yes'],
      ['Merge / Split / Compress', 'Yes', 'Yes'],
      ['Native mobile app', 'Yes', 'Web only (works on mobile)'],
    ],
    whenToPick: {
      us: [
        'You convert daily and hate hitting hourly limits.',
        'You want OCR on scanned documents for free.',
        'You value the strict 1-hour file-deletion promise.',
        'You do not want another account/email in your inbox.',
      ],
      them: [
        'You need a polished native iOS / Android app.',
        'You want built-in cloud storage (Dropbox / Google Drive) integrations.',
        'You need advanced PDF editing (redact, sign, form fill).',
      ],
    },
  },
  {
    slug: 'smallpdf',
    competitor: 'Smallpdf',
    title: 'Go Convert Documents vs Smallpdf',
    description:
      'Smallpdf vs Go Convert Documents — head-to-head on free daily limits, file-size caps, watermarks and privacy for 2026.',
    keywords: 'smallpdf alternative, smallpdf vs, smallpdf free comparison, unlimited pdf converter',
    intro:
      'Smallpdf has one of the cleanest UX in the PDF space, but the free plan famously caps you at two tasks per hour and rejects larger files. Go Convert Documents removes those limits in exchange for a simpler interface.',
    rows: [
      ['Free daily limit', '2 tasks per hour', 'None'],
      ['Signup required', 'For repeated use', 'Never'],
      ['Watermark on output', 'On some free tools', 'Never'],
      ['File retention', 'Varies', 'Auto-deleted after 1 hour'],
      ['Max file size (free)', '~15 MB', '100 MB'],
      ['OCR (scanned PDF → text)', 'Paid', 'Free'],
      ['Merge / Split / Compress', 'Yes', 'Yes'],
      ['PDF ↔ Office', 'Yes', 'Yes'],
      ['Watermark PDF', 'Yes', 'Yes'],
      ['Protect / Unlock PDF', 'Yes', 'Yes'],
      ['Native mobile app', 'Yes', 'Web only'],
    ],
    whenToPick: {
      us: [
        'You want unlimited free conversions — no hourly ceiling.',
        'You need OCR without paying for Smallpdf Pro.',
        'You care about the 1-hour auto-delete privacy guarantee.',
      ],
      them: [
        'You want built-in e-signature workflow.',
        'You already use Smallpdf inside Microsoft Teams / Google Workspace add-ons.',
      ],
    },
  },
  {
    slug: 'pdf24',
    competitor: 'PDF24',
    title: 'Go Convert Documents vs PDF24 Tools',
    description:
      'PDF24 vs Go Convert Documents — modern web-first converter vs old-school toolbox. See which is right for you.',
    keywords: 'pdf24 alternative, pdf24 vs, pdf24 tools comparison, modern pdf converter',
    intro:
      'PDF24 is a veteran of the space — a massive toolbox with a functional (if dated) UI and a Windows desktop client. Go Convert Documents is a cleaner, faster, browser-only alternative with a smaller but complete feature set.',
    rows: [
      ['Interface style', 'Utility / dated', 'Modern (2026)'],
      ['Signup required', 'No', 'No'],
      ['Ads', 'Yes', 'None'],
      ['Speed on large PDFs', 'Slow at times', 'Fast'],
      ['Watermark on output', 'None', 'None'],
      ['File retention', 'Varies', '1 hour, auto-deleted'],
      ['OCR', 'Yes', 'Yes'],
      ['Merge / Split / Compress', 'Yes', 'Yes'],
      ['Protect / Unlock / Watermark', 'Yes', 'Yes'],
      ['Native mobile app', 'Yes', 'Web only'],
      ['Windows desktop client', 'Yes', 'No'],
    ],
    whenToPick: {
      us: [
        'You want a modern, minimalist interface without ads.',
        'You need conversions quickly on mobile without installing anything.',
      ],
      them: [
        'You want an offline desktop application on Windows.',
        'You need very niche PDF tools (e.g. batch OCR training).',
      ],
    },
  },
  {
    slug: 'sejda',
    competitor: 'Sejda',
    title: 'Go Convert Documents vs Sejda',
    description:
      'Sejda vs Go Convert Documents — free-tier limits, editing depth, and privacy compared.',
    keywords: 'sejda alternative, sejda vs, sejda free limit, pdf editor comparison',
    intro:
      'Sejda is a genuinely powerful PDF editor with a generous but capped free tier (typically three tasks per day, 200 pages max). Go Convert Documents is pure conversion / utilities — no editor — but with no daily cap and simpler ergonomics.',
    rows: [
      ['Free daily limit', '3 tasks / 200 pages / day', 'None'],
      ['Real PDF text editor', 'Yes', 'No'],
      ['Signup required', 'Optional', 'Never'],
      ['Watermark on output', 'None', 'None'],
      ['File retention', '5 hours (opt-out)', '1 hour (fixed)'],
      ['OCR', 'Yes', 'Yes'],
      ['Merge / Split / Compress', 'Yes', 'Yes'],
      ['Protect / Unlock', 'Yes', 'Yes'],
      ['Native mobile app', 'No', 'Web only'],
    ],
    whenToPick: {
      us: [
        'You do many small conversions per day and hit the 3-task limit.',
        'You want the strictest possible file-retention window (1 hour).',
      ],
      them: [
        'You need to edit PDF text directly, not just convert.',
        'You need advanced sign / redact features.',
      ],
    },
  },
];

export function findComparison(slug) {
  return COMPARISONS.find((c) => c.slug === slug);
}
